import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";
import { AnalysisTreeProto } from "build-ts/analysis";
import { getNodeData, getTreeById, statefulAnalysisTree } from "services/AnalysisTreeServices";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { AnalysisTreeSessionKey, StatefulResponse } from "build-ts/stateful_api";
import { Header } from "build-ts/metadata";
import { StatefulRequestBuilder } from "containers/stateful/StatefulRequests";
import { v4 as uuidv4 } from "uuid";
import { listCustomAgents } from "services/CustomAgentServices";

interface AgentChatContextType {
	trees: AnalysisTreeProto[];
	getTrees: (treeId: string) => Promise<void>;
	loadTrees: (treeHeaders: Header[]) => Promise<AnalysisTreeSessionKey[]>;
	nodeDataMap: Map<string, AnalysisTreeNodeProto | null>;
	setNodeDataMap: React.Dispatch<React.SetStateAction<Map<string, AnalysisTreeNodeProto | null>>>;
	getNodeKgObservationData: (node: AnalysisTreeNodeProto) => Promise<AnalysisTreeNodeProto | null>;
	chatId: string | undefined;
	setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
	listOfNodes: Map<string, AnalysisTreeNodeProto>;
	setListOfNodes: React.Dispatch<React.SetStateAction<Map<string, AnalysisTreeNodeProto>>>;
	agentList: Header[];
	isLoadingAgentList: boolean;
	fetchAgentList: () => Promise<void>;
}


// Create the context
const AgentChatContext = createContext<AgentChatContextType | undefined>(undefined);

// Provider props interface
interface AgentChatContextProviderProps {
  children?: ReactNode;
  treeIds?: string[];
}

// Provider component
export const AgentChatContextProvider: React.FC<AgentChatContextProviderProps> = ({ 
  children, 
  treeIds = []
}) => {
	const [trees, setTrees] = useState<AnalysisTreeProto[]>([]);
	const [nodeDataMap, setNodeDataMap] = useState<Map<string, AnalysisTreeNodeProto | null>>(new Map<string, AnalysisTreeNodeProto | null>());
	const [chatId, setChatId] = useState<string | undefined>(undefined);
	const [listOfNodes, setListOfNodes] = useState<Map<string, AnalysisTreeNodeProto>>(new Map<string, AnalysisTreeNodeProto>());
	const [agentList, setAgentList] = useState<Header[]>([]);
	const [isLoadingAgentList, setIsLoadingAgentList] = useState(false);

	const fetchAgentList = async () => {
		setIsLoadingAgentList(true);
		try {
			const agents = await listCustomAgents(
				-1, // all jobs
				1, // page 1
				"LAST_MODIFIED", // sort by last modified
				false, // sort ascending
				true, // includeScheduleActivity
				"" // activeSchedule
			);
			if (agents) {
				setAgentList(agents?.headers || []);
			}
		} catch (error) {
			console.error("Error fetching agents:", error);
		} finally {
			setIsLoadingAgentList(false);
		}
	};

	const getTrees = async (treeId : string) => {
		if(trees.find(tree => tree.header?.id === treeId)){
			return;
		}
		const tree = await getTreeById(treeId, true, false, false, false);
		if(tree){
			setTrees(prev => [...prev, tree]);
		}
		if(tree?.nodes){
			for(const node of tree?.nodes){
				if(node){
					setListOfNodes(prev => {
						const newMap = new Map<string, AnalysisTreeNodeProto>(prev);
						newMap.set(node.id!, node);
						return newMap;
					});
				}
			}
		}
	};

	// Function to load the trees, this is used to get the tree sessions for the chat
	const loadTrees = async (treeHeaders: Header[]): Promise<AnalysisTreeSessionKey[]> => {
    const treeSessions: AnalysisTreeSessionKey[] = [];

    for(const treeHeader of treeHeaders){
      // Generate UUID
      const transactionId = uuidv4();
      const requestBuilder = new StatefulRequestBuilder(transactionId);
      const gettTreeRequest = requestBuilder.LoadTreeRequest(treeHeader?.id!);

      const response: StatefulResponse = await statefulAnalysisTree(gettTreeRequest);
      if(response){
        treeSessions.push(AnalysisTreeSessionKey.create({
          tree_id: treeHeader?.id!,
          state_key: {
            transaction_id: transactionId
          }
        }));
      }
    }
    return treeSessions;
  };

	// Function to get the kg observation data for a node
	const getNodeKgObservationData = async (node: AnalysisTreeNodeProto) => {
		const response = await getNodeData(node);
		if(response){
			setNodeDataMap(prev => {
				const newMap = new Map<string, AnalysisTreeNodeProto | null>(prev);
				newMap.set(node.id!, response);
				return newMap;
			});
			return response;
		}
		return null;
	};

  return (
    <AgentChatContext.Provider 
			value={{
				trees,
				getTrees,
				loadTrees,
				nodeDataMap,
				setNodeDataMap,
				getNodeKgObservationData,
				chatId,
				setChatId,
				listOfNodes,
				setListOfNodes,
				agentList,
				isLoadingAgentList,
				fetchAgentList,
			}}
		>
			{children}
    </AgentChatContext.Provider>
  );
};

// Custom hook to use the context
export const useAgentChat = () => {
  const context = useContext(AgentChatContext);
  
  if (!context) {
    throw new Error("useAgentChat must be used within an AgentChatContextProvider");
  }
  
  return context;
};
